import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import { Slider } from '../components/ui/slider';
import { Coins, Loader2, Plus, Eye, DollarSign, Lock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Stakeholders tab inside the Event Manager. Lets the admin:
 *   - Create the per-event NFT Ticket Offering (base price, supply, hype, reserve %, status)
 *   - Edit + flip status (draft → open → closed → settled)
 *   - View the cap table
 *   - Settle the offering (distribute net profit pro-rata after reserve)
 */
export default function EventStakeholdersTab({ eventId }) {
  const { token } = useAuth();
  const [data, setData] = useState({ offering: null, holdings: [] });
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showSettle, setShowSettle] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ base_price_cents: 50000, max_supply: 10, hype_factor_cents: 0, eventism_reserve_pct: 0.30, description: '', terms: '' });
  const [editForm, setEditForm] = useState({});
  const [settleForm, setSettleForm] = useState({ net_profit_cents: 0 });

  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/events/${eventId}/stakeholder-offering`, { headers });
      setData(r.data);
      if (r.data.offering) {
        setEditForm({
          base_price_cents: r.data.offering.base_price_cents,
          max_supply: r.data.offering.max_supply,
          hype_factor_cents: r.data.offering.hype_factor_cents,
          status: r.data.offering.status,
          description: r.data.offering.description || '',
        });
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load'); }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, token]);

  useEffect(() => { load(); }, [load]);

  const fmt = (cents) => `$${((cents || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const create = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/admin/events/${eventId}/stakeholder-offering`, form, { headers });
      toast.success('Offering created (status=draft)');
      setShowCreate(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setSubmitting(false);
  };

  const save = async (patch) => {
    try {
      await axios.patch(`${API}/admin/events/${eventId}/stakeholder-offering`, patch, { headers });
      toast.success('Saved');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const settle = async () => {
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/admin/events/${eventId}/stakeholder-offering/settle`, settleForm, { headers });
      toast.success(`Distributed ${fmt(r.data.total_distributed_cents)} to ${r.data.shares_sold} NFT Tickets`);
      setShowSettle(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setSubmitting(false);
  };

  if (loading) return <div className="text-center py-8"><Loader2 className="w-6 h-6 animate-spin mx-auto text-muted-foreground" /></div>;

  if (!data.offering) {
    return (
      <Card data-testid="stakeholders-empty">
        <CardContent className="p-8 text-center space-y-3">
          <Coins className="w-10 h-10 text-fuchsia-400 mx-auto" />
          <h3 className="font-bold text-lg">No NFT Ticket Offering yet</h3>
          <p className="text-sm text-muted-foreground max-w-md mx-auto">Create an offering to let your invited stakeholders deploy capital into this event and earn pro-rata profit-share on settlement.</p>
          <Button onClick={() => setShowCreate(true)} className="bg-gradient-to-r from-indigo-600 to-fuchsia-600" data-testid="create-offering-btn">
            <Plus className="w-4 h-4 mr-1.5" /> Create offering
          </Button>
        </CardContent>

        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogContent data-testid="create-offering-dialog">
            <DialogHeader>
              <DialogTitle>Create NFT Ticket Offering</DialogTitle>
              <DialogDescription>Sets the initial price + supply. You can edit and flip to "open" once you're ready for stakeholders to buy.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-semibold uppercase text-muted-foreground">Base price (USD)</label>
                <Input type="number" value={form.base_price_cents / 100} onChange={(e) => setForm({ ...form, base_price_cents: Math.round(parseFloat(e.target.value || 0) * 100) })} data-testid="offering-base-price-input" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase text-muted-foreground">Max supply (NFT Tickets)</label>
                <Input type="number" value={form.max_supply} onChange={(e) => setForm({ ...form, max_supply: parseInt(e.target.value) || 0 })} data-testid="offering-max-supply-input" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase text-muted-foreground">Hype factor (USD added when fully sold)</label>
                <Input type="number" value={form.hype_factor_cents / 100} onChange={(e) => setForm({ ...form, hype_factor_cents: Math.round(parseFloat(e.target.value || 0) * 100) })} placeholder="0" data-testid="offering-hype-input" />
                <p className="text-[10px] text-muted-foreground mt-1">Surge pricing. Final NFT Ticket costs base + hype. 0 = flat pricing.</p>
              </div>
              <div>
                <div className="flex justify-between items-baseline mb-2">
                  <label className="text-xs font-semibold uppercase text-muted-foreground">Eventism reserve %</label>
                  <span className="text-sm font-bold font-mono text-fuchsia-300">{Math.round(form.eventism_reserve_pct * 100)}% Eventism · {Math.round((1 - form.eventism_reserve_pct) * 100)}% holders</span>
                </div>
                <Slider value={[form.eventism_reserve_pct * 100]} onValueChange={(v) => setForm({ ...form, eventism_reserve_pct: v[0] / 100 })} min={15} max={95} step={1} data-testid="offering-reserve-slider" />
                <p className="text-[10px] text-muted-foreground mt-1">Default 30% to Eventism, 70% to backers. Min 15%, max 95%. Locks once offering closes.</p>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase text-muted-foreground">Description (shown to stakeholders)</label>
                <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Why this event is a great deploy..." data-testid="offering-description-input" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button onClick={create} disabled={submitting} className="bg-gradient-to-r from-indigo-600 to-fuchsia-600" data-testid="submit-create-offering">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Create offering
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </Card>
    );
  }

  const off = data.offering;
  const remaining = off.max_supply - (off.shares_sold || 0);

  return (
    <div className="space-y-4" data-testid="offering-active">
      {/* Status row */}
      <Card>
        <CardContent className="p-5">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
            <div>
              <Badge variant="outline" className="mb-2">{off.status?.toUpperCase()}</Badge>
              <h3 className="text-lg font-bold flex items-center gap-2"><Coins className="w-5 h-5 text-fuchsia-400" /> NFT Ticket Offering</h3>
            </div>
            <div className="flex gap-2 flex-wrap">
              {off.status === 'draft' && <Button size="sm" onClick={() => save({ status: 'open' })} className="bg-emerald-600 hover:bg-emerald-700" data-testid="status-open-btn">Open for buying</Button>}
              {off.status === 'open' && <Button size="sm" variant="outline" onClick={() => save({ status: 'closed' })} data-testid="status-close-btn"><Lock className="w-4 h-4 mr-1.5" /> Close offering</Button>}
              {off.status === 'closed' && <Button size="sm" onClick={() => { setSettleForm({ net_profit_cents: 0 }); setShowSettle(true); }} className="bg-emerald-600 hover:bg-emerald-700" data-testid="settle-btn"><DollarSign className="w-4 h-4 mr-1.5" /> Settle &amp; pay out</Button>}
              <Button size="sm" variant="ghost" asChild><a href={`/stakeholder/event/${eventId}`} target="_blank" rel="noopener noreferrer"><Eye className="w-4 h-4 mr-1.5" /> View as stakeholder</a></Button>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <div><div className="text-[10px] uppercase text-muted-foreground">Base price</div><div className="font-bold">{fmt(off.base_price_cents)}</div></div>
            <div><div className="text-[10px] uppercase text-muted-foreground">Current price</div><div className="font-bold text-fuchsia-300">{fmt(data.current_price_cents)}</div></div>
            <div><div className="text-[10px] uppercase text-muted-foreground">Sold / Supply</div><div className="font-bold">{off.shares_sold} / {off.max_supply} <span className="text-xs text-muted-foreground">({remaining} left)</span></div></div>
            <div><div className="text-[10px] uppercase text-muted-foreground">Total raised</div><div className="font-bold text-emerald-400">{fmt(off.total_raised_cents)}</div></div>
          </div>
          {off.status === 'settled' && (
            <div className="mt-4 rounded-md bg-emerald-500/10 border border-emerald-500/30 p-3 text-sm">
              ✅ Settled · Net profit {fmt(off.net_profit_cents)} · Payout per NFT {fmt(off.payout_per_share_cents)}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit form (only when not settled) */}
      {off.status !== 'settled' && (
        <Card>
          <CardContent className="p-5 space-y-3">
            <h3 className="font-semibold">Edit offering</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="text-xs font-semibold uppercase text-muted-foreground">Base price (USD)</label>
                <Input type="number" value={(editForm.base_price_cents || 0) / 100} onChange={(e) => setEditForm({ ...editForm, base_price_cents: Math.round(parseFloat(e.target.value || 0) * 100) })} />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase text-muted-foreground">Max supply</label>
                <Input type="number" value={editForm.max_supply || 0} onChange={(e) => setEditForm({ ...editForm, max_supply: parseInt(e.target.value) || 0 })} />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase text-muted-foreground">Hype factor (USD)</label>
                <Input type="number" value={(editForm.hype_factor_cents || 0) / 100} onChange={(e) => setEditForm({ ...editForm, hype_factor_cents: Math.round(parseFloat(e.target.value || 0) * 100) })} />
              </div>
            </div>
            <div>
              <div className="flex justify-between items-baseline mb-2">
                <label className="text-xs font-semibold uppercase text-muted-foreground">Eventism reserve % {off.status === 'closed' || off.status === 'settled' ? <Lock className="w-3 h-3 inline ml-1" /> : ''}</label>
                <span className="text-sm font-bold font-mono text-fuchsia-300">{Math.round((editForm.eventism_reserve_pct ?? 0.30) * 100)}% Eventism · {Math.round((1 - (editForm.eventism_reserve_pct ?? 0.30)) * 100)}% holders</span>
              </div>
              <Slider
                value={[(editForm.eventism_reserve_pct ?? 0.30) * 100]}
                onValueChange={(v) => setEditForm({ ...editForm, eventism_reserve_pct: v[0] / 100 })}
                min={15} max={95} step={1}
                disabled={off.status === 'closed' || off.status === 'settled'}
                data-testid="edit-reserve-slider"
              />
            </div>
            <Button size="sm" onClick={() => save({ base_price_cents: editForm.base_price_cents, max_supply: editForm.max_supply, hype_factor_cents: editForm.hype_factor_cents, eventism_reserve_pct: editForm.eventism_reserve_pct })} data-testid="save-offering-btn">Save changes</Button>
          </CardContent>
        </Card>
      )}

      {/* Cap table */}
      <Card>
        <CardContent className="p-5">
          <h3 className="font-semibold mb-3">Cap table — {data.holdings?.length || 0} holders</h3>
          {data.holdings?.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">No NFT Tickets sold yet.</p>
          ) : (
            <div className="space-y-1.5 text-sm" data-testid="admin-cap-table">
              {data.holdings.map((h) => (
                <div key={h.holding_id} className="flex justify-between items-center py-1.5 border-b border-border/50 last:border-0">
                  <span className="text-sm font-mono">{h.user_email}</span>
                  <div className="flex gap-4 text-xs">
                    <span>Paid <strong className="font-mono">{fmt(h.price_paid_cents)}</strong></span>
                    {h.payout_cents != null && <span>Paid out <strong className="text-emerald-400 font-mono">{fmt(h.payout_cents)}</strong></span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={showSettle} onOpenChange={setShowSettle}>
        <DialogContent data-testid="settle-dialog">
          <DialogHeader>
            <DialogTitle>Settle offering &amp; distribute payouts</DialogTitle>
            <DialogDescription>Enter the FINAL net profit (after vendor invoices, refunds, and Eventism platform fees). Each NFT Ticket = 1 / {off.max_supply} of the holder pool — un-subscribed slices stay with Eventism.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold uppercase text-muted-foreground">Net profit pool (USD)</label>
              <Input type="number" value={settleForm.net_profit_cents / 100} onChange={(e) => setSettleForm({ net_profit_cents: Math.round(parseFloat(e.target.value || 0) * 100) })} data-testid="settle-amount-input" />
            </div>
            <div className="rounded-md bg-slate-900/50 border border-border p-3 text-sm space-y-1">
              <div className="flex justify-between"><span className="text-muted-foreground">Net profit</span><span className="font-mono">{fmt(settleForm.net_profit_cents)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Eventism reserve ({Math.round((off.eventism_reserve_pct ?? 0.30) * 100)}%)</span><span className="font-mono text-fuchsia-300">{fmt(Math.round(settleForm.net_profit_cents * (off.eventism_reserve_pct ?? 0.30)))}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Full holder pool ({Math.round((1 - (off.eventism_reserve_pct ?? 0.30)) * 100)}%)</span><span className="font-mono">{fmt(Math.round(settleForm.net_profit_cents * (1 - (off.eventism_reserve_pct ?? 0.30))))}</span></div>
              <div className="flex justify-between border-t border-border pt-1 mt-1"><span className="text-muted-foreground">Per NFT Ticket <span className="text-[10px]">(1 of {off.max_supply})</span></span><span className="font-bold font-mono text-emerald-400">{fmt(Math.floor(settleForm.net_profit_cents * (1 - (off.eventism_reserve_pct ?? 0.30)) / Math.max(1, off.max_supply)))}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">× {off.shares_sold} subscribed NFT Tickets =</span><span className="font-bold font-mono text-emerald-400">{fmt(off.shares_sold * Math.floor(settleForm.net_profit_cents * (1 - (off.eventism_reserve_pct ?? 0.30)) / Math.max(1, off.max_supply)))}</span></div>
              <div className="flex justify-between text-xs"><span className="text-muted-foreground italic">+ Eventism keeps unsubscribed slice</span><span className="font-mono text-fuchsia-300">{fmt(Math.round(settleForm.net_profit_cents * (1 - (off.eventism_reserve_pct ?? 0.30))) - off.shares_sold * Math.floor(settleForm.net_profit_cents * (1 - (off.eventism_reserve_pct ?? 0.30)) / Math.max(1, off.max_supply)))}</span></div>
            </div>
            <p className="text-[11px] text-muted-foreground">Each NFT Ticket = 1 / {off.max_supply} of the holder pool. Subscription is currently <strong>{Math.round(off.shares_sold / Math.max(1, off.max_supply) * 100)}%</strong> ({off.shares_sold} of {off.max_supply}). Un-subscribed NFT Tickets' value stays with Eventism.</p>
            <p className="text-[11px] text-muted-foreground">⚠️ This action is final. Each holder's wallet will be credited and they'll be emailed.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSettle(false)}>Cancel</Button>
            <Button onClick={settle} disabled={submitting} className="bg-emerald-600 hover:bg-emerald-700" data-testid="confirm-settle-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Distribute payouts
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
